import * as MaplibreGrid from '@sofarocean/maplibre-grid';
import MapboxContext from '@sofarocean/mapbox-context/dist/contexts/MapboxContext';
import { range } from 'lodash';
import mapboxgl from 'mapbox-gl';
import { useContext, useEffect } from 'react';

import { useMapUiContext } from '@/context/MapUiContext';
import { fontFamily } from '@/theme';

const ZOOM_LEVELS = [
    { minZoom: 7, gridSize: 1 },
    { minZoom: 5, maxZoom: 7, gridSize: 5 },
    { minZoom: 2, maxZoom: 5, gridSize: 10 },
    { maxZoom: 2, gridSize: 30 },
];

export const Graticules = () => {
    const { map } = useContext(MapboxContext);
    const { baseLayer } = useMapUiContext();

    // use hex because we need to append an alpha byte for the label markers
    const color = baseLayer === 'satellite' ? '#ffffff' : '#000000';

    useEffect(() => {
        if (!map) {
            return;
        }

        const grids = ZOOM_LEVELS.map(
            ({ minZoom, maxZoom, gridSize }) =>
                new MaplibreGrid.Grid({
                    gridWidth: gridSize,
                    gridHeight: gridSize,
                    units: 'degrees',
                    minZoom,
                    maxZoom,
                    paint: {
                        'line-opacity': 0.1,
                        'line-color': color,
                    },
                })
        );
        grids.forEach((g) => map.addControl(g));

        const latMarkers = range(-85, 86).map((lat) => {
            const element = document.createElement('span');
            element.className = 'graticuleMarker';
            element.innerText = `${Math.abs(lat)}°`;
            element.style.display = 'none';
            return new mapboxgl.Marker({ offset: [-4, 0], anchor: 'right', element }).setLngLat([0, lat]).addTo(map);
        });
        const lngMarkers = range(-179, 181).map((lng) => {
            const element = document.createElement('span');
            element.className = 'graticuleMarker';
            element.innerText = `${Math.abs(lng)}°`;
            element.style.display = 'none';
            return new mapboxgl.Marker({ offset: [0, 0], anchor: 'bottom', element }).setLngLat([lng, 0]).addTo(map);
        });

        const repositionMarkers = () => {
            const bounds = map.getBounds();
            if (!bounds) return;

            // when we're so far zoomed out that the world wraps, positioning longitude gets inconsistent, so we'll just
            // fix lat labels to the intl date line to avoid flickering
            const wrapped = bounds.getEast() - bounds.getWest() >= 360;
            const { gridSize } = ZOOM_LEVELS.find(
                ({ minZoom, maxZoom }) =>
                    (!minZoom || minZoom <= map.getZoom()) && (!maxZoom || maxZoom > map.getZoom())
            )!;
            latMarkers.forEach((m) => {
                const { lat } = m.getLngLat();
                const el = m.getElement();
                if (lat % gridSize !== 0 || lat > bounds.getNorth() || lat < bounds.getSouth()) {
                    el.style.display = 'none';
                    return;
                }
                m.setLngLat([wrapped ? 180 : bounds.getEast(), lat]);
                el.style.display = 'initial';
            });
            lngMarkers.forEach((m) => {
                const { lng } = m.getLngLat();
                const el = m.getElement();
                if (lng % gridSize !== 0 || lng > bounds.getEast() || lng <= bounds.getWest()) {
                    el.style.display = 'none';
                    return;
                }
                m.setLngLat([lng, bounds.getSouth()]);
                el.style.display = 'initial';
            });
        };
        map.on('move', repositionMarkers);

        // MapLibre.Grid often doesn't render initially, but this forces
        // it to. Since this also triggers repositionMarkers, we'll initialize
        // our marker positions.
        setTimeout(() => map.fire('move'));

        return () => {
            map.off('move', repositionMarkers);
            try {
                latMarkers.forEach((m) => m.remove());
                lngMarkers.forEach((m) => m.remove());
                grids.forEach((g) => map.removeControl(g));
            } catch {
                // ignore
            }
        };
    }, [color, map]);
    return <style>{`.graticuleMarker {font-family: ${fontFamily}; font-size: 10px; color: ${color}66`}</style>;
};
